import { useMemo, useState } from 'react';
import { THAI_DENOMINATIONS, countDenominations } from '@sushimai/core';
import { api } from '../lib/api.ts';
import { baht, bahtShort } from '../lib/format.ts';
import { openDrawer, printDrawerSummary } from '../actions.ts';
import { useStore } from '../store.tsx';
import { Alert, AsyncButton, Field, NumPad, padToSatang, useToast } from '../ui.tsx';
import type { CloseResult, DrawerSession } from '../lib/types.ts';

/**
 * ปิดรอบลิ้นชัก — นับเงินทีละชนิด
 *
 * ★ นับเป็นใบ ไม่ใช่กรอกยอดรวม ★
 * ถ้าให้กรอกยอดรวม พนักงานจะกรอกยอดที่ระบบบอกว่า "ควรมี" แล้วเงินขาดจะไม่มีวันโผล่
 * การนับทีละชนิดทำให้ตัวเลขมาจากเงินจริงในลิ้นชัก
 *
 * ★ ยอดที่ควรมีจะแสดงหลังกดปิดรอบเท่านั้น ★ ไม่แสดงตอนกำลังนับ
 * ด้วยเหตุผลเดียวกัน
 */
export function CloseDrawerForm({
  drawer,
  onClosed,
  onCancel,
}: {
  drawer: DrawerSession;
  onClosed: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const store = useStore();
  const toast = useToast();

  const [counts, setCounts] = useState<Record<number, number>>({});
  const [keep, setKeep] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CloseResult | null>(null);

  const counted = useMemo(
    () =>
      countDenominations(
        new Map(Object.entries(counts).map(([d, q]) => [Number(d), q])),
      ),
    [counts],
  );

  const keepSatang = padToSatang(keep) ?? 0;

  const close = async () => {
    setError(null);
    try {
      const closed = await api.post<CloseResult>(`/drawer-sessions/${drawer.id}/close`, {
        denominations: Object.fromEntries(
          Object.entries(counts).filter(([, qty]) => qty > 0),
        ),
        keepSatang,
        note,
      });
      setResult(closed);

      // ปิดรอบต้องเปิดลิ้นชักเพื่อหยิบเงินนำส่งออกมา
      await openDrawer(store, true, 'drawer_session_close');

      const problem = await printDrawerSummary(store, {
        docNo: drawer.docNo,
        businessDate: drawer.businessDate,
        openingFloatSatang: drawer.openingFloatSatang,
        expectedSatang: closed.expectedSatang,
        countedSatang: closed.countedSatang,
        varianceSatang: closed.varianceSatang,
        keepSatang: closed.keepSatang,
      });
      if (problem) toast(`ใบสรุปพิมพ์ไม่ออก — ${problem}`, 'warn');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ปิดรอบไม่สำเร็จ');
    }
  };

  /* ── ปิดรอบเรียบร้อย ─────────────────────────────────────── */
  if (result) {
    const variance = result.varianceSatang;
    return (
      <>
        <div
          className={`alert ${variance === 0 ? 'ok' : 'warn'}`}
          style={{ fontSize: '1.3rem', fontWeight: 700 }}
        >
          {variance === 0
            ? 'เงินตรงพอดี'
            : variance > 0
              ? `เงินเกิน ${baht(variance)} บาท`
              : `เงินขาด ${baht(Math.abs(variance))} บาท`}
        </div>

        <div className="total-row">
          <span>เงินที่ควรมี</span>
          <span className="v">{baht(result.expectedSatang)}</span>
        </div>
        <div className="total-row">
          <span>นับได้จริง</span>
          <span className="v">{baht(result.countedSatang)}</span>
        </div>
        <div className="total-row">
          <span>เก็บไว้เป็นเงินทอนพรุ่งนี้</span>
          <span className="v">{baht(result.keepSatang)}</span>
        </div>
        <div className="total-row grand">
          <span>นำส่ง</span>
          <span className="v">{baht(result.countedSatang - result.keepSatang)}</span>
        </div>

        {variance !== 0 && (
          <p style={{ color: 'var(--ink-2)', marginTop: 12 }}>
            ระบบแจ้งเจ้าของร้านเรื่องเงิน{variance > 0 ? 'เกิน' : 'ขาด'}แล้ว
          </p>
        )}

        <button className="btn big wide" style={{ marginTop: 20 }} onClick={() => void onClosed()}>
          เสร็จ
        </button>
      </>
    );
  }

  /* ── หน้านับเงิน ─────────────────────────────────────────── */
  return (
    <>
      {error && <Alert>{error}</Alert>}

      <Field label="นับเงินในลิ้นชักทีละชนิด">
        <div className="rowlist">
          {[...THAI_DENOMINATIONS].reverse().map((denom) => {
            const qty = counts[denom] ?? 0;
            return (
              <div key={denom} className="rowitem">
                <span className="grow">
                  <strong>{bahtShort(denom)}</strong>
                  <div className="sub">{denom >= 2000 ? 'ธนบัตร' : 'เหรียญ'}</div>
                </span>
                <button
                  className="qty-btn"
                  onClick={() => setCounts((c) => ({ ...c, [denom]: Math.max(0, qty - 1) }))}
                >
                  −
                </button>
                <input
                  className="money"
                  inputMode="numeric"
                  style={{ width: 84, minHeight: 48, padding: '0 10px', fontSize: '1.1rem' }}
                  value={qty === 0 ? '' : String(qty)}
                  placeholder="0"
                  onChange={(e) => {
                    const n = Number(e.target.value.replace(/\D/g, ''));
                    setCounts((c) => ({ ...c, [denom]: Number.isFinite(n) ? n : 0 }));
                  }}
                />
                <button
                  className="qty-btn"
                  onClick={() => setCounts((c) => ({ ...c, [denom]: qty + 1 }))}
                >
                  +
                </button>
                <span className="v" style={{ width: 96, textAlign: 'right' }}>
                  {qty > 0 ? baht(denom * qty) : '—'}
                </span>
              </div>
            );
          })}
        </div>
      </Field>

      <div className="total-row grand">
        <span>นับได้ทั้งหมด</span>
        <span className="v">{baht(counted)}</span>
      </div>

      <Field label="เก็บไว้เป็นเงินทอนของพรุ่งนี้ (บาท)">
        <div className="amount-display">{baht(keepSatang)}</div>
        <NumPad onChange={setKeep} />
      </Field>

      {keepSatang > counted && (
        <Alert>เงินที่จะเก็บไว้มากกว่าเงินที่นับได้จริง</Alert>
      )}

      <div className="total-row">
        <span>นำส่ง</span>
        <span className="v">{baht(Math.max(0, counted - keepSatang))}</span>
      </div>

      <Field label="หมายเหตุ">
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="ไม่บังคับ — ถ้าเงินขาดหรือเกิน ใส่เหตุผลไว้ตรงนี้"
        />
      </Field>

      <div className="action-row">
        <button className="btn ghost" onClick={onCancel}>ยังไม่ปิด</button>
        <AsyncButton
          className="btn big"
          onClick={close}
          disabled={counted === 0 || keepSatang > counted}
        >
          ปิดรอบ
        </AsyncButton>
      </div>
    </>
  );
}
